import { create } from 'zustand';

// One polite live region for the whole app (DESIGN_SYSTEM.md §16, spec §7.2).
// Throttled channels (e.g. record-check results) announce at most once per
// interval; the latest message wins when the window reopens.

interface AnnouncerState {
  message: string;
  /** Changes on every announcement so identical text is re-read. */
  nonce: number;
}

export const useAnnouncer = create<AnnouncerState>(() => ({ message: '', nonce: 0 }));

const lastByChannel = new Map<string, number>();
const pendingByChannel = new Map<string, ReturnType<typeof setTimeout>>();

function speak(message: string) {
  useAnnouncer.setState((s) => ({ message, nonce: s.nonce + 1 }));
}

export function announce(message: string, options: { channel?: string; throttleMs?: number } = {}) {
  const { channel, throttleMs = 0 } = options;
  if (!channel || throttleMs <= 0) {
    speak(message);
    return;
  }
  const now = Date.now();
  const last = lastByChannel.get(channel) ?? 0;
  clearTimeout(pendingByChannel.get(channel));
  if (now - last >= throttleMs) {
    lastByChannel.set(channel, now);
    speak(message);
    return;
  }
  pendingByChannel.set(
    channel,
    setTimeout(
      () => {
        lastByChannel.set(channel, Date.now());
        speak(message);
      },
      throttleMs - (now - last),
    ),
  );
}

export function cancelAnnouncements(channel: string) {
  clearTimeout(pendingByChannel.get(channel));
  pendingByChannel.delete(channel);
}
